import { Component } from 'preact';
import { onMaintenanceChange, acknowledgeUpdateComplete } from '../utils/maintenanceState';
import { isWeb } from '../utils/platform';
import RuneSpinner from './RuneSpinner';
import { t } from '../localization/t';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, ACCENT_GOLD, DIALOG_STYLE, BEVELED_BTN, FourCorners,
} from '../lib/medievalTheme';

function quitApp() {
  fetch('/api/app/quit', { method: 'POST' }).catch(() => {});
}

function reloadApp() {
  window.location.reload();
}

// Shared button style for the overlay's escape-hatch actions.
const OVERLAY_BTN_STYLE = {
  ...BEVELED_BTN,
  padding: '0.7cqw 1.4cqw',
  fontSize: '0.78cqw',
  cursor: 'pointer',
};

// Two visual states:
//
//   - Scheduled (banner):  pinned to the top of the canvas, counts
//     down to the planned restart. Subtle but visible while the
//     player keeps playing. Like a transit-system service notice.
//
//   - Imminent (overlay):  full-screen takeover when the server is
//     actively going down (SIGTERM or admin "begin now"). Stops
//     interaction so a player doesn't lose an action mid-deploy.
//
// The component is mounted once at the app root and self-manages
// visibility. No parent state changes required.

function formatRemaining(ms) {
  if (ms <= 0) return t('momentarily');
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return t('in {h}h {m}m', { h, m: String(m).padStart(2, '0') });
  if (m > 0) return t('in {m}m {s}s', { m, s: String(s).padStart(2, '0') });
  return t('in {s}s', { s });
}

function formatClock(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Lower bound (ms) at which the banner re-appears as a "final
// reminder" even if the user has dismissed it. The notice can't be
// dismissed again during this window. It's the last polite warning
// before the imminent overlay takes over.
const FINAL_REMINDER_MS = 10_000;

export default class MaintenanceBanner extends Component {
  state = {
    scheduled: null,
    imminent: null,
    updatePhase: 'normal',
    now: Date.now(),
    dismissedSetAt: null,
  };

  componentDidMount() {
    this._unsub = onMaintenanceChange(({ scheduled, imminent, updatePhase }) => {
      this.setState({ scheduled, imminent, updatePhase: updatePhase || 'normal' });
    });
    this._tick = setInterval(() => this.setState({ now: Date.now() }), 1000);
  }

  componentWillUnmount() {
    if (this._unsub) this._unsub();
    if (this._tick) clearInterval(this._tick);
  }

  handleDismiss = () => {
    const setAt = this.state.scheduled?.setAt || null;
    this.setState({ dismissedSetAt: setAt });
  };

  handleContinue = () => {
    acknowledgeUpdateComplete();
    // Renderer reload re-establishes the WS against the freshly-
    // deployed server cleanly.
    window.location.reload();
  };

  render() {
    const { scheduled, imminent, updatePhase, now } = this.state;

    // Update underway / complete take priority. They're a full-screen
    // lock and override both the scheduled banner and the earlier
    // imminent countdown. Once we're in either state, nothing else
    // about the maintenance state matters until the player reloads.
    if (updatePhase === 'underway' || updatePhase === 'complete') {
      const isComplete = updatePhase === 'complete';
      return (
        <div
          className="fixed inset-0 z-[450] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.94)', backdropFilter: 'blur(10px)' }}
        >
          <div className="relative w-full max-w-[36cqw] p-8 text-center" style={DIALOG_STYLE}>
            <FourCorners />
            <div
              className="text-[0.625cqw] font-semibold mb-3"
              style={{ color: isComplete ? '#86efac' : `${GOLD} 0.6)` }}
            >
              {isComplete ? t('Update Complete') : t('Update Underway')}
            </div>
            <h2
              className="text-xl font-bold arena-heading mb-4"
              style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
            >
              {isComplete ? t('Realm is back online') : t('Hold the line…')}
            </h2>

            {isComplete ? null : (
              <div className="flex justify-center mb-4">
                <RuneSpinner />
              </div>
            )}

            <p className="text-sm leading-relaxed mb-5" style={{ color: TEXT_BODY }}>
              {isComplete
                ? t('The new version is live. Reload to continue where you left off.')
                : t('The server is updating. Your client will resume automatically once the new version is live.')}
            </p>

            <div className="flex justify-center gap-3">
              {isComplete ? (
                <button
                  type="button"
                  className="arena-heading font-semibold"
                  style={{ ...BEVELED_BTN, color: ACCENT_GOLD, padding: '0.7cqw 1.4cqw', fontSize: '0.78cqw' }}
                  onClick={this.handleContinue}
                >
                  {t('Continue')}
                </button>
              ) : isWeb() ? null : (
                <button
                  type="button"
                  className="arena-heading font-semibold"
                  style={{ ...BEVELED_BTN, color: TEXT_BODY, padding: '0.7cqw 1.4cqw', fontSize: '0.78cqw' }}
                  onClick={() => fetch('/api/app/quit', { method: 'POST' }).catch(() => {})}
                >
                  {t('Quit')}
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (!scheduled && !imminent) return null;

    if (imminent) {
      const targetMs = new Date(imminent.at).getTime();
      const remaining = Math.max(0, targetMs - now);
      const isDown = remaining <= 0;
      return (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)' }}
        >
          <div className="relative w-full max-w-[34cqw] p-7" style={DIALOG_STYLE}>
            <FourCorners />
            <div className="text-center">
              <div
                className="text-[0.625cqw] font-semibold mb-2"
                style={{ color: `${GOLD} 0.6)` }}
              >
                {t('Server Restarting')}
              </div>
              <h2
                className="text-xl font-bold arena-heading mb-3"
                style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
              >
                {t('Going Down {when}', { when: formatRemaining(remaining) })}
              </h2>
              <p className="text-sm leading-relaxed mb-5" style={{ color: TEXT_BODY }}>
                {imminent.message || t('The server is restarting for an update.')} {t('Your client will automatically reconnect once the new version is live.')}
              </p>

              {/* Escape-hatch actions. During the countdown the player
                  can leave on their own terms ("Quit Now") rather than
                  watching a timer they can't affect. Once the server
                  is actually down, we also expose "Try Reconnect" so
                  they can manually trigger a renderer reload instead
                  of waiting for the auto-reconnect heuristic. */}
              <div className="flex justify-center gap-3">
                {isDown ? (
                  <button
                    type="button"
                    className="arena-heading font-semibold"
                    style={{ ...OVERLAY_BTN_STYLE, color: ACCENT_GOLD }}
                    onClick={reloadApp}
                  >
                    {t('Try Reconnect')}
                  </button>
                ) : null}
                {isWeb() ? null : (
                  <button
                    type="button"
                    className="arena-heading font-semibold"
                    style={{ ...OVERLAY_BTN_STYLE, color: TEXT_BODY }}
                    onClick={quitApp}
                  >
                    {isDown ? t('Quit') : t('Quit Now')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    const targetMs = new Date(scheduled.scheduledAt).getTime();
    const remaining = targetMs - now;
    if (remaining < -5 * 60_000) return null;

    const { dismissedSetAt } = this.state;
    const finalReminder = remaining > 0 && remaining <= FINAL_REMINDER_MS;
    const userDismissed = dismissedSetAt && dismissedSetAt === scheduled.setAt;
    // Render rules: hide if the user dismissed AND we're not yet in
    // the final-reminder window. The final reminder always shows and
    // can't be dismissed (no close button rendered in that state).
    if (userDismissed && !finalReminder) return null;

    const urgent = remaining < 15 * 60_000;
    const accent = finalReminder ? '#ef4444' : (urgent ? ACCENT_GOLD : `${GOLD} 0.55)`);
    const labelColor = finalReminder ? '#fca5a5' : (urgent ? ACCENT_GOLD : `${GOLD} 0.7)`);
    const labelText = finalReminder ? t('Restart Imminent') : t('Scheduled Maintenance');

    return (
      <div
        className="fixed left-1/2 top-3 z-[200] -translate-x-1/2 pointer-events-none"
        style={{ width: 'auto', maxWidth: '40cqw' }}
      >
        <div
          className="pointer-events-auto relative flex items-stretch"
          style={{
            ...DIALOG_STYLE,
            borderRadius: 'var(--radius-lg)',
            border: `1px solid ${accent}`,
            boxShadow: finalReminder
              ? '0 4px 24px rgba(239,68,68,0.4), 0 0 32px rgba(239,68,68,0.2)'
              : `0 4px 16px rgba(0,0,0,0.5), 0 0 24px ${urgent ? 'rgba(212,168,67,0.25)' : 'transparent'}`,
            // Subtle pulse during the final-reminder window so the eye
            // catches it even if the player is mid-action.
            animation: finalReminder ? 'mt-pulse 1s ease-in-out infinite' : 'none',
          }}
        >
          <div className="flex flex-col px-[1.05cqw] py-[0.65cqw] gap-[0.15cqw] min-w-0 flex-1">
            <div className="flex items-baseline gap-2">
              <span
                className="text-[0.625cqw] font-semibold shrink-0"
                style={{ color: labelColor }}
              >
                {labelText}
              </span>
              {scheduled.message ? (
                <span className="text-[0.7cqw] truncate" style={{ color: TEXT_PRIMARY }}>
                  · {scheduled.message}
                </span>
              ) : null}
            </div>

            <div className="text-sm font-semibold flex items-baseline gap-1.5" style={{ color: TEXT_PRIMARY }}>
              {finalReminder ? (
                <span style={{ color: '#fca5a5' }}>{t('Server restart {when}', { when: formatRemaining(remaining) })}</span>
              ) : (
                <>
                  <span>{t('Server restart {when}', { when: formatRemaining(remaining) })}</span>
                  <span className="text-xs font-normal" style={{ color: TEXT_BODY }}>
                    ({formatClock(scheduled.scheduledAt)})
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Close button. Hidden during the final reminder window so
              the player can't accidentally dismiss the "actually
              starting now" warning. */}
          {!finalReminder ? (
            <button
              type="button"
              className="shrink-0 self-start mt-[0.6cqw] mr-[0.6cqw] flex items-center justify-center rounded-full transition-all"
              title={t('Dismiss')}
              style={{
                width: '1.3cqw',
                height: '1.3cqw',
                background: 'rgba(0,0,0,0.5)',
                border: `1px solid ${GOLD} 0.3)`,
                color: TEXT_BODY,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.18)`; e.currentTarget.style.color = TEXT_PRIMARY; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.5)'; e.currentTarget.style.color = TEXT_BODY; }}
              onClick={this.handleDismiss}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.7cqw', height: '0.7cqw' }}>
                <path d="m6 6 12 12" />
                <path d="m18 6-12 12" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>
    );
  }
}
